import "../button/btn.css"
import "../input-text-base/index.css"
import "./index.css"

import {
  CalendarDate,
  type DateValue,
  getLocalTimeZone,
  parseDate,
  today as todayCalendar,
} from "@internationalized/date"
import Icon from "@utilities/Icon"
import type { CombineAriaPropsWithCustomProps } from "@utilities/reactAriaProps"
import classNames from "classnames"
import React, { type ReactNode, useContext, useState } from "react"
import { CalendarStateContext } from "react-aria-components/Calendar"
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker as AriaDatePicker,
  type DatePickerProps as AriaDatePickerProps,
  DateSegment,
  Group,
  Label,
  Popover,
  Text,
} from "react-aria-components/DatePicker"
import { Dialog } from "react-aria-components/Dialog"
import { ListBox, ListBoxItem } from "react-aria-components/ListBox"

const _today = todayCalendar(getLocalTimeZone())
const MONTHS = Array.from({ length: 12 }, (_, i) => i)
const YEARS = Array.from({ length: 131 }, (_, i) => _today.year - 100 + i)
const shortMonthFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
})
const longMonthFormatter = new Intl.DateTimeFormat(undefined, { month: "long" })

export type DatePickerSize = "md" | "lg"

export type DatePickerDateValue = DateValue | string

function toCalendarDate(
  input: DatePickerDateValue | null | undefined
): DateValue | null | undefined {
  if (input === null) return null
  if (input === undefined) return undefined

  if (typeof input === "object" && "calendar" in input) {
    return input
  }

  return parseDate(input)
}

export interface DatePickerProps {
  /** Helper text displayed below the component. Styled as error text when `invalid` is `true`. */
  description?: string
  /** Disables the input and calendar button. */
  disabled?: boolean
  /** Sets the first day of the week in the calendar. Defaults to locale-appropriate value. */
  firstDayOfWeek?: "fri" | "mon" | "sat" | "sun" | "thu" | "tue" | "wed"
  /** If `true`, the `label` text is rendered as an `aria-label` instead of a visible label element. */
  hideLabel?: boolean
  /** Whether the input is in an invalid state. */
  invalid?: boolean
  /** Callback that returns `true` for dates that should be visible but not selectable. */
  isDateUnavailable?: (date: DateValue) => boolean
  /** Accessible label for the field. */
  label: string
  /** Maximum selectable date. Accepts a DateValue or ISO date string. */
  max?: DatePickerDateValue
  /** Minimum selectable date. Accepts a DateValue or ISO date string. */
  min?: DatePickerDateValue
  /** Called when the selected date changes. */
  onChange?: (value: DateValue | null) => void
  /** If `true`, allows the value to be read but not changed. */
  readOnly?: boolean
  /** If `true`, appends an asterisk at the end of the label text. */
  required?: boolean
  /** The size of the date picker. */
  size?: DatePickerSize
  /** The controlled value. Accepts a DateValue or ISO date string. */
  value?: DatePickerDateValue | null
}

type AriaDatePickerPropsToOmit =
  | "children"
  | "defaultOpen"
  | "defaultValue"
  | "granularity"
  | "hideTimeZone"
  | "hourCycle"
  | "pageBehavior"
  | "placeholderValue"
  | "shouldCloseOnSelect"
  | "shouldForceLeadingZeros"
  | "slot"

type AriaDatePickerPropsToInclude = "isOpen" | "name" | "onOpenChange"

export type DatePickerElementProps = CombineAriaPropsWithCustomProps<
  AriaDatePickerProps<DateValue>,
  DatePickerProps,
  AriaDatePickerPropsToOmit,
  AriaDatePickerPropsToInclude
>

function DatePickerOverlay({
  children,
  variant,
}: {
  children: ReactNode
  variant: "month" | "year"
}) {
  return (
    <div
      aria-label={`Select ${variant}`}
      aria-modal="true"
      className={`tds-date-picker-overlay tds-date-picker-overlay--${variant}`}
      role="dialog"
    >
      {children}
    </div>
  )
}

function DatePickerCalendarBody({
  activeOverlay,
  setActiveOverlay,
}: {
  activeOverlay: "month" | "year" | null
  setActiveOverlay: (overlay: "month" | "year" | null) => void
}) {
  // Read and drive Calendar's focused date through its state context. This
  // lets react-aria-components own the "open to selected month" behavior:
  // when the popover opens, Calendar mounts and auto-focuses the DatePicker's
  // current value (whether typed into the input segments or set via `value`).
  const state = useContext(CalendarStateContext)!
  const focusedDate = state.focusedDate
  const today = todayCalendar(getLocalTimeZone())

  function handleOverlaySelectionChange(
    keys: "all" | Set<number | string>,
    onSelect: (key: number) => void
  ) {
    if (keys === "all") return
    const [first] = Array.from(keys)
    if (first !== undefined) onSelect(Number(first))
  }

  function handleMonthSelect(monthIndex: number) {
    state.setFocusedDate(new CalendarDate(focusedDate.year, monthIndex + 1, 1))
    setActiveOverlay(null)
  }

  function handleYearSelect(year: number) {
    state.setFocusedDate(new CalendarDate(year, focusedDate.month, 1))
    setActiveOverlay(null)
  }

  return (
    <>
      <header className="tds-date-picker-calendar-header">
        <div className="tds-date-picker-calendar-heading">
          <Button
            aria-expanded={activeOverlay === "month"}
            aria-haspopup="dialog"
            className="tds-date-picker-calendar-overlay-trigger"
            onPress={() =>
              setActiveOverlay(activeOverlay === "month" ? null : "month")
            }
            slot={null}
          >
            {longMonthFormatter.format(
              new Date(2000, focusedDate.month - 1, 1)
            )}
          </Button>
          <Button
            aria-expanded={activeOverlay === "year"}
            aria-haspopup="dialog"
            className="tds-date-picker-calendar-overlay-trigger"
            onPress={() =>
              setActiveOverlay(activeOverlay === "year" ? null : "year")
            }
            slot={null}
          >
            {focusedDate.year}
          </Button>
        </div>
        {activeOverlay ? (
          <Button
            aria-label={`Close ${activeOverlay} picker`}
            className="tds-date-picker-calendar-nav"
            onPress={() => setActiveOverlay(null)}
            slot={null}
          >
            <Icon aria-hidden symbol="general#x" />
          </Button>
        ) : (
          <>
            <Button
              aria-label="Go to today"
              className="tds-date-picker-calendar-nav tds-date-picker-calendar-nav--lg"
              style={{
                visibility:
                  focusedDate.month === today.month &&
                  focusedDate.year === today.year
                    ? "hidden"
                    : undefined,
              }}
              onPress={() => {
                state.setFocusedDate(today)
                state.setFocused(true)
              }}
              slot={null}
            >
              {/* TODO: Add to icon library and use Icon once icon is decided on */}
              <svg
                aria-hidden
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="symbol"
              >
                <path d="M13.444 14.222H2.556V5.667h10.888zm0-12.444h-.777V.222h-1.556v1.556H4.889V.222H3.333v1.556h-.777a1.55 1.55 0 0 0-1.548 1.555L1 14.222a1.555 1.555 0 0 0 1.556 1.556h10.888A1.56 1.56 0 0 0 15 14.222V3.333a1.56 1.56 0 0 0-1.556-1.555" />
                <circle cx="8" cy="9.75" r="2.25" />
              </svg>
            </Button>
            <Button
              aria-label="Previous month"
              className="tds-date-picker-calendar-nav"
              slot="previous"
            >
              <Icon aria-hidden symbol="general#left-chevron" />
            </Button>
            <Button
              aria-label="Next month"
              className="tds-date-picker-calendar-nav"
              slot="next"
            >
              <Icon aria-hidden symbol="general#right-chevron" />
            </Button>
          </>
        )}
      </header>
      <div className="tds-date-picker-calendar-body">
        {activeOverlay === "month" && (
          <DatePickerOverlay variant="month">
            <ListBox
              aria-label="Months"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focusing the active item on dialog open is required per ARIA authoring practices
              autoFocus
              className="tds-date-picker-overlay-list"
              disallowEmptySelection
              escapeKeyBehavior="none"
              layout="grid"
              onSelectionChange={(keys) =>
                handleOverlaySelectionChange(keys, handleMonthSelect)
              }
              selectedKeys={[String(focusedDate.month - 1)]}
              selectionMode="single"
            >
              {MONTHS.map((i) => {
                const date = new Date(2000, i, 1)
                const shortMonth = shortMonthFormatter.format(date)
                const longMonth = longMonthFormatter.format(date)

                return (
                  <ListBoxItem
                    className="tds-date-picker-overlay-cell"
                    id={String(i)}
                    key={i}
                    aria-label={longMonth}
                    textValue={longMonth}
                  >
                    {shortMonth}
                  </ListBoxItem>
                )
              })}
            </ListBox>
          </DatePickerOverlay>
        )}
        {activeOverlay === "year" && (
          <DatePickerOverlay variant="year">
            <ListBox
              aria-label="Years"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focusing the active item on dialog open is required per ARIA authoring practices
              autoFocus
              className="tds-date-picker-overlay-list"
              disallowEmptySelection
              escapeKeyBehavior="none"
              layout="grid"
              onSelectionChange={(keys) =>
                handleOverlaySelectionChange(keys, handleYearSelect)
              }
              selectedKeys={[String(focusedDate.year)]}
              selectionMode="single"
            >
              {YEARS.map((year) => (
                <ListBoxItem
                  className="tds-date-picker-overlay-cell"
                  id={String(year)}
                  key={year}
                  textValue={String(year)}
                >
                  {year}
                </ListBoxItem>
              ))}
            </ListBox>
          </DatePickerOverlay>
        )}
        <CalendarGrid className="tds-date-picker-calendar-grid">
          <CalendarGridHeader className="tds-date-picker-calendar-grid-header">
            {(day) => (
              <CalendarHeaderCell className="tds-date-picker-calendar-header-cell">
                {day}
              </CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody className="tds-date-picker-calendar-grid-body">
            {(date) => (
              <CalendarCell
                className="tds-date-picker-calendar-cell-button"
                date={date}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
      </div>
    </>
  )
}

/**
 * A date picker allows a user to select a particular date.
 * The input supports keyboard entry of individual day, month, and year segments;
 * the calendar offers month and year quick-pick overlays plus a "go to today" shortcut.
 *
 * The date picker automatically displays the value and calendar from the user's locale,
 * and does not require a specific date format.
 *
 * Values are exchanged as `DateValue` from `@internationalized/date`, but
 * `value`, `min`, and `max` also accept ISO date strings (`"YYYY-MM-DD"`) for
 * convenience. Restrict the selectable range with `min`/`max`, or mark
 * individual dates as unavailable via `isDateUnavailable`.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-datepicker--docs | Storybook Documentation}
 */
export function DatePicker({
  className,
  description,
  disabled,
  firstDayOfWeek,
  hideLabel,
  invalid,
  isDateUnavailable,
  label,
  max,
  min,
  onChange,
  readOnly,
  required,
  size = "md",
  value,
  ...restProps
}: DatePickerElementProps) {
  const convertedValue = toCalendarDate(value)
  const convertedMin = toCalendarDate(min) ?? undefined
  const convertedMax = toCalendarDate(max) ?? undefined

  const combinedClassName = classNames(
    "tds-field tds-date-picker",
    {
      "tds-date-picker--lg": size === "lg",
      "tds-field--lg": size === "lg",
    },
    className
  )

  const [activeOverlay, setActiveOverlay] = useState<"month" | "year" | null>(
    null
  )

  return (
    <AriaDatePicker
      {...restProps}
      aria-label={hideLabel ? label : undefined}
      className={combinedClassName}
      isDateUnavailable={isDateUnavailable}
      isDisabled={disabled}
      isInvalid={invalid}
      isReadOnly={readOnly}
      isRequired={required}
      maxValue={convertedMax}
      minValue={convertedMin}
      onChange={onChange ?? undefined}
      onOpenChange={(isOpen) => {
        if (!isOpen) setActiveOverlay(null)
      }}
      value={convertedValue}
    >
      {!hideLabel && <Label className="tds-field-label">{label}</Label>}
      <Group className="tds-field-control tds-date-picker-field">
        <DateInput className="tds-date-picker-input">
          {(segment) => (
            <DateSegment
              className={
                segment.type === "literal"
                  ? "tds-field-date-segment-separator"
                  : "tds-field-date-segment"
              }
              segment={segment}
            />
          )}
        </DateInput>
        <Button className="tds-btn tds-btn--infield tds-date-picker-button">
          <Icon aria-hidden symbol="general#calendar" />
        </Button>
      </Group>
      <Popover
        className={classNames("tds-date-picker-popover", {
          "tds-date-picker-popover--lg": size === "lg",
        })}
        offset={2}
      >
        <Dialog>
          <Calendar
            className="tds-date-picker-calendar"
            firstDayOfWeek={firstDayOfWeek}
          >
            <DatePickerCalendarBody
              activeOverlay={activeOverlay}
              setActiveOverlay={setActiveOverlay}
            />
          </Calendar>
        </Dialog>
      </Popover>
      {description && (
        <Text
          elementType="p"
          className="tds-field-description"
          slot="description"
        >
          <Icon
            aria-hidden
            className="tds-field-description-invalid-icon"
            symbol="general#exclamation-triangle"
          />
          {description}
        </Text>
      )}
    </AriaDatePicker>
  )
}

DatePicker.displayName = "DatePicker"
